import {
  CASE_PLAN_ACCESS_PERMISSIONS,
  CASE_PLAN_EDIT_PLAN_PERMISSION,
  PORTAL_REENTRY_PLAN_TYPE,
  PORTAL_REENTRY_PREVIEW_PLAN_TYPE,
  REENTRY_PLAN_FEATURE,
  REENTRY_PLAN_TYPE,
  REENTRY_PREVIEW_PLAN_TYPE,
  REENTRY_VISIBILITY_PLAN_TYPES,
  mapEntitledPlanTypesToPortalApi,
} from '../constants/reentry-planning.constants';

const LAST_REFRESHED_TIME_OPTIONS = {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

export const formatLastRefreshedText = (timestamp) => {
  if (!timestamp) {
    return '';
  }

  return `Last Refreshed ${timestamp.toLocaleTimeString('en-US', LAST_REFRESHED_TIME_OPTIONS)}`;
};

const normalizePermissionName = (permissionName) => permissionName?.toLowerCase() ?? '';

const CASE_PLAN_ACCESS_PERMISSIONS_SET = new Set(
  CASE_PLAN_ACCESS_PERMISSIONS.map((permission) => permission.toLowerCase()),
);

export const hasCasePlanAccess = (permissions, permissionIds) =>
  permissions?.some(
    (permission) =>
      CASE_PLAN_ACCESS_PERMISSIONS_SET.has(normalizePermissionName(permission.permissionName)) &&
      permissionIds?.includes(permission.permissionId),
  );

export const hasCasePlanEditPermission = (permissions, permissionIds) =>
  permissions?.some(
    (permission) =>
      normalizePermissionName(permission.permissionName) ===
      CASE_PLAN_EDIT_PLAN_PERMISSION.toLowerCase() &&
      permissionIds?.includes(permission.permissionId),
  );

export const isReentryPlanFeatureEnabled = (locationFeatures) =>
  locationFeatures?.find((feature) => feature.featureName === REENTRY_PLAN_FEATURE)?.status === true;

const isPlanTypeFeatureEnabled = (locationFeatures, planTypeKey) =>
  locationFeatures?.find((feature) => feature.featureName === planTypeKey)?.status === true;

export const resolveEntitledPlanTypesFromFeatures = (locationFeatures) =>
  REENTRY_VISIBILITY_PLAN_TYPES.filter((planTypeKey) =>
    isPlanTypeFeatureEnabled(locationFeatures, planTypeKey),
  );


export const getEffectiveActiveFeatures = (location, allLocations = []) => {
  if (Array.isArray(location?.activeFeatures) && location.activeFeatures.length > 0) {
    return location.activeFeatures;
  }

  if (location?.parentLocationId) {
    const parent = allLocations.find((item) => item.locationId === location.parentLocationId);
    return parent?.activeFeatures || [];
  }

  return [];
};

const ACTIVE_FEATURE_TO_VISIBILITY_PLAN_TYPE = {
  [REENTRY_PREVIEW_PLAN_TYPE]: REENTRY_PREVIEW_PLAN_TYPE,
  [REENTRY_PLAN_TYPE]: REENTRY_PLAN_TYPE,
  [PORTAL_REENTRY_PREVIEW_PLAN_TYPE]: REENTRY_PREVIEW_PLAN_TYPE,
  [PORTAL_REENTRY_PLAN_TYPE]: REENTRY_PLAN_TYPE,
};

const resolveVisibilityPlanTypesFromActiveFeatures = (activeFeatures) => {
  const entitledPlanTypes = new Set();

  (activeFeatures || []).forEach((featureName) => {
    const visibilityKey = ACTIVE_FEATURE_TO_VISIBILITY_PLAN_TYPE[featureName];
    if (visibilityKey) {
      entitledPlanTypes.add(visibilityKey);
    }
  });

  return REENTRY_VISIBILITY_PLAN_TYPES.filter((planTypeKey) => entitledPlanTypes.has(planTypeKey));
};

const filterLocationsByScope = (locations, scopeLocationIds) => {
  if (!scopeLocationIds) {
    return locations;
  }

  return locations.filter((location) => scopeLocationIds.has(location.locationId));
};

export const resolveEntitledPlanTypesFromLocations = (locations, scopeLocationIds) => {
  if (!Array.isArray(locations) || locations.length === 0) {
    return [];
  }

  const scopedLocations = filterLocationsByScope(locations, scopeLocationIds);
  const entitledPlanTypes = new Set();

  scopedLocations.forEach((location) => {
    const activeFeatures = getEffectiveActiveFeatures(location, locations);
    resolveVisibilityPlanTypesFromActiveFeatures(activeFeatures).forEach((planTypeKey) => {
      entitledPlanTypes.add(planTypeKey);
    });
  });

  return REENTRY_VISIBILITY_PLAN_TYPES.filter((planTypeKey) => entitledPlanTypes.has(planTypeKey));
};

export const hasReentryPlanTypeInLocations = (locations) =>
  resolveEntitledPlanTypesFromLocations(locations ?? []).length > 0;

export const resolvePortalPlanTypesFromLocations = (locations, scopeLocationIds) =>
  mapEntitledPlanTypesToPortalApi(resolveEntitledPlanTypesFromLocations(locations, scopeLocationIds));

export const createViewPlanClickHandler = (onViewPlan, plan) => () => {
  onViewPlan(plan);
};

export const REENTRY_PLANS_LOCATION_COLUMN = 'location';

const FACILITY_LOCATION_TYPE_ID = 3;
const HOUSING_UNIT_LOCATION_TYPE_ID = 15;

const compareByLocationName = (a, b) =>
  String(a.locationName).localeCompare(String(b.locationName));

const pickFacilitiesSorted = (locations) =>
  locations
    .filter((item) => item.locationTypeId === FACILITY_LOCATION_TYPE_ID)
    .sort(compareByLocationName);

const pickHousingUnitsForFacility = (locations, facility) =>
  locations
    .filter(
      (item) =>
        item.parentLocationId === facility.locationId &&
        item.locationTypeId === HOUSING_UNIT_LOCATION_TYPE_ID,
    )
    .sort(compareByLocationName);

const buildFacilityWithHousingUnits = (locations, facility) => [
  facility,
  ...pickHousingUnitsForFacility(locations, facility),
];

const toNonEmptyLocationName = (item) => {
  const name = item?.locationName;
  return name != null && String(name).trim() !== '' ? String(name) : null;
};

export const getLocationFilterOptions = (locations) => {
  if (!Array.isArray(locations) || locations.length === 0) {
    return [];
  }
  return pickFacilitiesSorted(locations)
    .flatMap((facility) => buildFacilityWithHousingUnits(locations, facility))
    .map(toNonEmptyLocationName)
    .filter(Boolean);
};

export const mapLocationFilterToLocationIds = (filters, locations = []) => {
  const filter = Array.isArray(filters)
    ? filters.find((item) => item.columnName === REENTRY_PLANS_LOCATION_COLUMN)
    : null;

  if (!filter?.value) {
    return undefined;
  }

  const locationNames = String(filter.value)
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);

  if (locationNames.length === 0) {
    return undefined;
  }

  const locationIds = locations
    .filter(
      (item) =>
        locationNames.includes(item.locationName) &&
        (item.locationTypeId === FACILITY_LOCATION_TYPE_ID ||
          item.locationTypeId === HOUSING_UNIT_LOCATION_TYPE_ID),
    )
    .map((item) => item.locationId);

  return locationIds.length > 0 ? locationIds.join(',') : undefined;
};
